import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Empty, Spin, Typography } from 'antd';

import {
    getTaskCoordinates,
    isGeneratorTask,
    loadYandexMaps
} from '../utils/yandexMaps';
import {
    formatMoscowDateTime,
    normalizeStatusKey
} from '../utils/taskManager';

import './TaskManagerMap.css';

const { Text } = Typography;

const DEFAULT_CENTER = [55.75, 37.62];
const DEFAULT_ZOOM = 9;
const DEFAULT_STATUS_COLOR = '#1677ff';

const escapeHtml = value =>
    String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const statusColor = (status, colorMap = {}) =>
    colorMap[normalizeStatusKey(status)] || DEFAULT_STATUS_COLOR;

const TaskManagerMap = ({
    tasks = [],
    apiKey = '',
    colorMap = {},
    onTaskClick
}) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const clustererRef = useRef(null);
    const iconLayoutRef = useRef(null);
    const onTaskClickRef = useRef(onTaskClick);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        onTaskClickRef.current = onTaskClick;
    }, [onTaskClick]);

    const points = useMemo(
        () =>
            (tasks || [])
                .map(task => {
                    const coords = getTaskCoordinates(task);
                    return coords ? { task, coords } : null;
                })
                .filter(Boolean),
        [tasks]
    );

    const legendItems = useMemo(() => {
        const seen = new Map();
        points.forEach(({ task }) => {
            const name = task.status_name || 'Без статуса';
            const key = normalizeStatusKey(name);
            if (!seen.has(key)) {
                seen.set(key, {
                    name,
                    color: statusColor(name, colorMap)
                });
            }
        });
        return Array.from(seen.values());
    }, [points, colorMap]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!containerRef.current) {
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const ymaps = await loadYandexMaps(apiKey);
                if (cancelled || !containerRef.current) {
                    return;
                }

                if (mapRef.current) {
                    mapRef.current.destroy();
                    mapRef.current = null;
                    clustererRef.current = null;
                    iconLayoutRef.current = null;
                }

                const map = new ymaps.Map(
                    containerRef.current,
                    {
                        center: DEFAULT_CENTER,
                        zoom: DEFAULT_ZOOM,
                        controls: [
                            'zoomControl',
                            'typeSelector',
                            'fullscreenControl'
                        ]
                    },
                    { suppressMapOpenBlock: true }
                );

                iconLayoutRef.current =
                    ymaps.templateLayoutFactory.createClass(
                        `<div class="tm-ymap-pin{% if properties.isGenerator %} is-generator{% endif %}" style="background-color:$[properties.markerColor];">
                            {% if properties.isGenerator %}
                              <span class="tm-ymap-pin-icon" title="Генератор">⚡</span>
                            {% endif %}
                            <span class="tm-ymap-pin-text">$[properties.iconContent]</span>
                         </div>`
                    );

                const clusterer = new ymaps.Clusterer({
                    preset: 'islands#invertedBlueClusterIcons',
                    groupByCoordinates: false,
                    clusterDisableClickZoom: false
                });

                map.geoObjects.add(clusterer);
                mapRef.current = map;
                clustererRef.current = clusterer;
                setReady(true);
                setLoading(false);
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err?.message ||
                            'Ошибка загрузки карты'
                    );
                    setLoading(false);
                    setReady(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            if (mapRef.current) {
                mapRef.current.destroy();
                mapRef.current = null;
                clustererRef.current = null;
                iconLayoutRef.current = null;
            }
            setReady(false);
        };
    }, [apiKey]);

    useEffect(() => {
        const map = mapRef.current;
        const clusterer = clustererRef.current;
        const iconLayout = iconLayoutRef.current;
        if (
            !ready ||
            !map ||
            !clusterer ||
            !iconLayout ||
            !window.ymaps
        ) {
            return;
        }

        const ymaps = window.ymaps;
        clusterer.removeAll();

        const placemarks = points.map(({ task, coords }) => {
            const number =
                task.task_number ||
                (task.fields || []).find(
                    f => f.system_name === 'Nomer_TT'
                )?.value ||
                `#${task.id}`;

            const title = task.title || 'Без названия';
            const status = task.status_name || '—';
            const assignee =
                task.assigned_user_fio || 'не назначен';
            const generator = isGeneratorTask(task);
            const color = statusColor(status, colorMap);

            const placemark = new ymaps.Placemark(
                coords,
                {
                    iconContent: String(number).slice(0, 14),
                    markerColor: color,
                    isGenerator: generator,
                    balloonContentHeader: escapeHtml(number),
                    balloonContentBody: `
                        <div style="max-width:280px;font:13px/1.45 sans-serif">
                          ${
                              generator
                                  ? '<div style="margin-bottom:6px;display:inline-flex;align-items:center;gap:4px;background:#fff7e6;color:#d48806;border:1px solid #ffd591;border-radius:6px;padding:2px 8px;font-size:12px;font-weight:600">⚡ Генератор</div>'
                                  : ''
                          }
                          <div style="font-weight:600;margin:0 0 6px">${escapeHtml(title)}</div>
                          <div>Статус: <span style="display:inline-block;padding:1px 8px;border-radius:10px;color:#fff;background:${escapeHtml(color)};font-size:12px">${escapeHtml(status)}</span></div>
                          <div style="margin-top:4px">Исполнитель: ${escapeHtml(assignee)}</div>
                          <div style="color:#8c8c8c;margin-top:4px">Создана: ${escapeHtml(formatMoscowDateTime(task.created_at))}</div>
                          <button type="button" data-tm-open="${task.id}" style="margin-top:10px;cursor:pointer;border:1px solid ${escapeHtml(color)};background:${escapeHtml(color)};color:#fff;border-radius:6px;padding:4px 10px">
                            Открыть задачу
                          </button>
                        </div>
                    `,
                    hintContent: `${generator ? '⚡ ' : ''}${number} · ${title}`
                },
                {
                    iconLayout,
                    iconShape: {
                        type: 'Rectangle',
                        coordinates: [
                            [-70, -34],
                            [70, 0]
                        ]
                    },
                    hideIconOnBalloonOpen: false
                }
            );

            placemark.events.add('balloonopen', () => {
                window.setTimeout(() => {
                    const btn = document.querySelector(
                        `button[data-tm-open="${task.id}"]`
                    );
                    if (!btn || btn.dataset.bound === '1') {
                        return;
                    }
                    btn.dataset.bound = '1';
                    btn.addEventListener('click', event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onTaskClickRef.current?.(task);
                    });
                }, 30);
            });

            return placemark;
        });

        clusterer.add(placemarks);

        if (points.length === 1) {
            map.setCenter(points[0].coords, 14, {
                duration: 200
            });
        } else if (points.length > 1) {
            try {
                map.setBounds(clusterer.getBounds(), {
                    checkZoomRange: true,
                    zoomMargin: 48,
                    duration: 200
                });
            } catch (_) {
                /* ignore */
            }
        }
    }, [points, ready, colorMap]);

    return (
        <div
            className="tm-ymap-wrap tm-ymap-wrap--fill"
            style={{
                position: 'relative',
                height: 'calc(100vh - 200px)',
                minHeight: 420,
                borderRadius: 10,
                overflow: 'hidden',
                border: '1px solid #e5e7eb',
                background: '#f5f7fb'
            }}
        >
            {loading ? (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(245,247,251,0.72)'
                    }}
                >
                    <Spin tip="Загрузка карты…" />
                </div>
            ) : null}

            {error ? (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 24,
                        background: '#f5f7fb'
                    }}
                >
                    <Empty
                        description={
                            <span>
                                {error}
                                <br />
                                <Text type="secondary">
                                    Добавьте YANDEX_MAPS_API_KEY в
                                    backend/.env и перезапустите
                                    сервер
                                </Text>
                            </span>
                        }
                    />
                </div>
            ) : null}

            {!loading && !error && points.length === 0 ? (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                    }}
                >
                    <Empty description="Нет задач с координатами" />
                </div>
            ) : null}

            <div
                ref={containerRef}
                style={{ width: '100%', height: '100%' }}
            />

            {!error ? (
                <div
                    style={{
                        position: 'absolute',
                        left: 12,
                        bottom: 12,
                        zIndex: 1,
                        background: 'rgba(255,255,255,0.92)',
                        borderRadius: 8,
                        padding: '6px 10px',
                        fontSize: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                >
                    На карте: {points.length} из {tasks.length}
                    {' · '}
                    <span title="Генератор">⚡</span> — генератор
                </div>
            ) : null}

            {!error && legendItems.length ? (
                <div className="tm-ymap-legend">
                    <div
                        style={{
                            fontWeight: 600,
                            marginBottom: 6
                        }}
                    >
                        Статусы
                    </div>
                    {legendItems.map(item => (
                        <div
                            key={item.name}
                            className="tm-ymap-legend-row"
                        >
                            <span
                                className="tm-ymap-legend-dot"
                                style={{
                                    background: item.color
                                }}
                            />
                            <span>{item.name}</span>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default TaskManagerMap;
